/**
 * React Query hooks for contractor tasks.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ContractorTask,
  type ApiRequestError,
  type StartTaskResponse,
  type StopTaskResponse,
  type CompleteTaskResponse,
} from '../lib/api';
import type { TaskStatus } from 'scheduler-types';

export interface UseTasksOptions {
  status?: TaskStatus[];
  limit?: number;
  offset?: number;
  enabled?: boolean;
}

export interface UseTasksResult {
  tasks: ContractorTask[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
  error: ApiRequestError | null;
  refetch: () => void;
}

/**
 * Fetch tasks assigned to the current contractor.
 */
export function useTasks(options: UseTasksOptions = {}): UseTasksResult {
  const { status, limit = 50, offset = 0, enabled = true } = options;

  const query = useQuery({
    queryKey: ['tasks', { status, limit, offset }],
    queryFn: () => api.getMyTasks({ status, limit, offset }),
    enabled,
    staleTime: 30_000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on 403/401
      if (error instanceof Error && 'status' in error) {
        const status = (error as ApiRequestError).status;
        if (status === 401 || status === 403) return false;
      }
      return failureCount < 3;
    },
  });

  return {
    tasks: query.data?.data ?? [],
    total: query.data?.pagination?.total ?? 0,
    hasMore: query.data?.pagination?.hasMore ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as ApiRequestError | null,
    refetch: query.refetch,
  };
}

/**
 * Fetch a single task by ID.
 */
export function useTask(taskId: string | null) {
  return useQuery({
    queryKey: ['task', taskId],
    queryFn: () => (taskId ? api.getTask(taskId) : null),
    enabled: !!taskId,
    staleTime: 30_000,
  });
}

/**
 * Start working on a task.
 */
export function useStartTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => api.startTask(taskId),
    onSuccess: (data: StartTaskResponse) => {
      // Invalidate task queries to refetch updated state
      queryClient.invalidateQueries({ queryKey: ['task', data.data.taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/**
 * Stop (pause) working on a task.
 */
export function useStopTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => api.stopTask(taskId),
    onSuccess: (data: StopTaskResponse) => {
      queryClient.invalidateQueries({ queryKey: ['task', data.data.taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/**
 * Complete a task.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => api.completeTask(taskId),
    onSuccess: (data: CompleteTaskResponse) => {
      queryClient.invalidateQueries({ queryKey: ['task', data.data.taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
